import {
  useCallback, useEffect, useRef, useState, useSyncExternalStore,
  type ButtonHTMLAttributes, type CSSProperties, type InputHTMLAttributes, type ReactNode,
  type RefObject, type TextareaHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ApiError, FaceViewer, PhoneInput, prettyPhone, useEscape } from '@limuzin/kit';
import type { RideStopView } from '@limuzin/passenger';
import { copyText } from './clipboard';
import { Icon, type IconName } from './material';

/**
 * The pieces the screens are built from, ported from
 * `apps/rider/lib/widgets/pieces.dart`, `fields.dart` and `route_dots.dart` — the
 * same names where the phone has one, so a screen here reads against its twin there.
 *
 * They are here rather than in `@limuzin/kit` because they are this product's
 * furniture, not the house style the panel shares.
 */

// --------------------------------------------------------------------- the card

/** The white card the whole design is built from. */
export function Panel({
  children,
  radius = 20,
  hero = false,
  flush = false,
  edge,
  className,
  style,
}: {
  children: ReactNode;
  radius?: 16 | 18 | 20;
  /** The design's big card: 24 round and 24 in. */
  hero?: boolean;
  flush?: boolean;
  /** A line round the card where its own colour is not enough to say where it ends. */
  edge?: string;
  className?: string;
  style?: CSSProperties;
}) {
  const classes = [
    'panel',
    radius === 18 ? 'r18' : radius === 16 ? 'r16' : '',
    hero ? 'hero' : '',
    flush ? 'flush' : '',
    edge ? 'edged' : '',
    className ?? '',
  ].filter(Boolean);
  return (
    <div
      className={classes.join(' ')}
      style={edge ? ({ ...style, '--edge': edge } as CSSProperties) : style}
    >
      {children}
    </div>
  );
}

/** The screen's own heading — «Новый заказ» and the line under it. */
export function ScreenTitle({
  title, subtitle, trailing,
}: { title: string; subtitle?: string; trailing?: ReactNode }) {
  return (
    <div>
      {trailing ? (
        <div className="screen-title-row">
          <h1 className="title">{title}</h1>
          {trailing}
        </div>
      ) : (
        <h1 className="title">{title}</h1>
      )}
      {subtitle && <p className="subtitle">{subtitle}</p>}
    </div>
  );
}

/**
 * A tab with a title that stays put and a list that scrolls under the hairline
 * beneath it — «Новый заказ», «Мои поездки», «Мои адреса».
 */
export function TitledScreen({
  title,
  subtitle,
  children,
  below,
  trailing,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  /** What sits between the title and the list, and does not scroll — the tabs of
   * «Мои поездки». Given it, the list has no hairline of its own. */
  below?: ReactNode;
  /** An action at the right of the title. */
  trailing?: ReactNode;
}) {
  return (
    <div className="screen">
      <div className="screen-head column">
        <ScreenTitle title={title} subtitle={subtitle} trailing={trailing} />
      </div>
      {below}
      <div className={below ? 'screen-scroll' : 'screen-scroll ruled'}>{children}</div>
    </div>
  );
}

/**
 * A screen pushed over the tabs — «Настройки», «Поддержка» — with the bar a pushed
 * screen wears: a back arrow, its name, and the hairline under them.
 */
export function Page({
  title,
  children,
  trailing,
  centred = false,
  fallback = '/',
  scroll = true,
}: {
  title: string;
  children: ReactNode;
  /** What the screen can do to itself, at the far end of its bar. */
  trailing?: ReactNode;
  /** «Поддержка»: the name in the middle, the way back on a disc of its own. */
  centred?: boolean;
  /** Where back leads when the page was opened straight from a link. */
  fallback?: string;
  scroll?: boolean;
}) {
  const back = useBack(fallback);
  return (
    <div className="page">
      <div className="column" style={{ flex: 'none' }}>
        <ScreenBar title={title} onBack={back} trailing={trailing} centred={centred} />
      </div>
      {scroll ? (
        <div className="screen-scroll">
          <div className="column">{children}</div>
        </div>
      ) : (
        children
      )}
    </div>
  );
}

/**
 * Back, the way a phone's back button goes: to wherever this was opened from, or —
 * for a page opened straight from a notification, with nothing behind it in this app
 * — to [fallback].
 */
export function useBack(fallback: string): () => void {
  const navigate = useNavigate();
  return useCallback(() => {
    const index = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (index > 0) navigate(-1);
    else navigate(fallback, { replace: true });
  }, [navigate, fallback]);
}

export function ScreenBar({
  title,
  onBack,
  trailing,
  centred = false,
}: {
  title: string;
  onBack: () => void;
  trailing?: ReactNode;
  centred?: boolean;
}) {
  return (
    <div className={centred ? 'screen-bar centred' : 'screen-bar'}>
      <button
        type="button"
        className={centred ? 'icon-btn big card' : 'icon-btn'}
        onClick={onBack}
        aria-label="Назад"
      >
        <Icon name="arrow_back" size={20} />
      </button>
      <h1>{title}</h1>
      {centred ? <span className="bar-side">{trailing}</span> : trailing}
    </div>
  );
}

// ------------------------------------------------------------------- buttons

type ButtonTone = 'stop' | 'quiet' | 'tint' | 'plain' | 'go';
type ButtonSize = 'card' | 'foot' | 'field' | 'tile';

/**
 * `PrimaryButton`: the full-width button at the foot of nearly every screen. Disabled
 * is a colour change rather than a removal — the passenger should see what they are
 * missing.
 */
export function Button({
  children,
  tone,
  size,
  busy = false,
  icon,
  className,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  tone?: ButtonTone;
  size?: ButtonSize;
  busy?: boolean;
  icon?: IconName;
}) {
  return (
    <button
      type="button"
      {...rest}
      className={['btn', tone ?? '', size ?? '', className ?? ''].filter(Boolean).join(' ')}
      disabled={rest.disabled || busy}
      style={busy ? { ...rest.style, background: toneGround(tone) } : rest.style}
    >
      {busy ? (
        <span className="spinner" />
      ) : (
        <>
          {icon && <Icon name={icon} size={18} />}
          {children}
        </>
      )}
    </button>
  );
}

/** A busy button keeps its colour and holds a spinner, rather than going grey. */
function toneGround(tone: ButtonTone | undefined): string {
  switch (tone) {
    case 'stop':
      return 'var(--stop-card)';
    case 'quiet':
      return 'var(--wash)';
    case 'tint':
      return 'var(--tint)';
    case 'plain':
      return 'var(--card)';
    case 'go':
      return 'var(--go-card)';
    default:
      return 'var(--accent)';
  }
}

/** `SoftButton` — the tinted secondary one: «Чат», «Повторить», «Добавить». */
export function Soft({
  children,
  tone,
  icon,
  dot = false,
  wide = false,
  className,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  tone?: 'stop' | 'go' | 'quiet';
  icon?: IconName;
  /** The red dot on the icon's corner — «Чат» with something in it not yet read. */
  dot?: boolean;
  wide?: boolean;
}) {
  return (
    <button
      type="button"
      {...rest}
      className={['soft', tone ?? '', wide ? 'wide' : '', className ?? ''].filter(Boolean).join(' ')}
    >
      {icon && (
        <span className="dotted">
          <Icon name={icon} size={16} />
          {dot && <span className="unread-dot pulse" style={{ top: -3, right: -4 }} />}
        </span>
      )}
      {children}
    </button>
  );
}

export function IconButton({
  icon,
  label,
  size = 20,
  tone,
  className,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  icon: IconName;
  label: string;
  size?: number;
  tone?: 'card' | 'go' | 'stop';
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      {...rest}
      className={['icon-btn', tone ?? '', className ?? ''].filter(Boolean).join(' ')}
    >
      <Icon name={icon} size={size} />
    </button>
  );
}

/** The round back button the design draws instead of a bar. */
export function BackDisc({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" className="back-disc" onClick={onClick} aria-label="Назад">
      <Icon name="arrow_back_ios_new" size={16} />
    </button>
  );
}

/** The mark that says a row opens something. */
export const Chevron = () => <Icon name="chevron_right" size={20} className="chevron" />;

// ------------------------------------------------------------------ messages

/**
 * What the server said, shown verbatim, in the failure line under a form. Every error
 * the API returns carries a Russian message written for the person who caused it.
 */
export function ErrorNote({ error }: { error: ApiError | string | null | undefined }) {
  const message = !error ? null : typeof error === 'string' ? error : error.message;
  useErrorHaptic(message);
  if (!message) return null;
  return (
    <div className="error-note" role="alert">
      {message}
    </div>
  );
}

/**
 * The phone's «no» in the hand as a failure line appears, or says something new —
 * `ErrorHaptic` in the phone app. A line is small and often below the thumb that
 * caused it, and a passenger who pressed and saw nothing happen presses again.
 *
 * Only a change is felt: a line already there when the screen opens is old news, and
 * one drawn again with the same words has said nothing new.
 */
export function useErrorHaptic(failure: string | null | undefined): void {
  const was = useRef(failure);
  useEffect(() => {
    if (failure && failure !== was.current) feelFailure();
    was.current = failure;
  }, [failure]);
}

/**
 * Two short pulses, the shape of Android's own «reject». Where the browser has no
 * vibration — Safari, a laptop — the failure is seen alone, as it was before.
 */
function feelFailure(): void {
  try {
    navigator.vibrate?.([30, 60, 30]);
  } catch {
    // Refused before any press on the page: nothing to feel, nothing broken.
  }
}

export function Note({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div className="note" style={style}>
      {children}
    </div>
  );
}

/** A card that says nothing is here, in the design's own words. */
export function EmptyCard({ title, hint }: { title: string; hint: string }) {
  return (
    <Panel className="empty-card">
      <h3 className="heading">{title}</h3>
      <p className="body">{hint}</p>
    </Panel>
  );
}

export function Spinner() {
  return (
    <div className="centred-spinner">
      <span className="spinner big" />
    </div>
  );
}

// -------------------------------------------------------------------- toasts

/*
 * A word about what just happened, gone by itself — the phone's snack bar, for the
 * refusals that have no form to stand under: a preorder that would not cancel, a
 * photograph the server turned away.
 */
/** A link at the end of a toast — a number to ring where something did not go. */
export interface ToastAction {
  label: string;
  href: string;
}

let shown: { id: number; text: string; action?: ToastAction } | null = null;
const heard = new Set<() => void>();
let timer = 0;

export function toast(text: string, options: { action?: ToastAction; ms?: number } = {}): void {
  shown = { id: Date.now(), text, ...(options.action ? { action: options.action } : {}) };
  heard.forEach((listener) => listener());
  window.clearTimeout(timer);
  timer = window.setTimeout(() => {
    shown = null;
    heard.forEach((listener) => listener());
  }, options.ms ?? 4000);
}

/** A failure said in a toast, where there is no form for an `ErrorNote` to sit under. */
export function showProblem(text: string, options: { action?: ToastAction; ms?: number } = {}): void {
  feelFailure();
  toast(text, options);
}

export function Toasts() {
  const now = useSyncExternalStore(
    (listener) => {
      heard.add(listener);
      return () => heard.delete(listener);
    },
    () => shown,
  );
  if (!now) return null;
  return (
    <div className="toast" role="status" key={now.id}>
      {now.text}
      {now.action && (
        <a className="toast-action" href={now.action.href}>
          {now.action.label}
        </a>
      )}
    </div>
  );
}

// ------------------------------------------------------------------- people

/**
 * The round initials that stand in for a photograph everywhere, and the photograph —
 * which a tap opens big, unless [enlarges] is off because the tap already means
 * something else, as on the passenger's own circle in «Профиль».
 */
export function Initials({
  text,
  size = 46,
  tint = false,
  src,
  enlarges = true,
}: {
  text: string;
  size?: number;
  /** The passenger's own: in the accent on its tint, as «Профиль» draws it. */
  tint?: boolean;
  src?: string | null;
  enlarges?: boolean;
}) {
  const [from, setFrom] = useState<DOMRect | null>(null);
  const style: CSSProperties = {
    width: size,
    height: size,
    fontSize: Math.round(size * 0.32),
  };
  if (tint) {
    style.background = 'var(--tint)';
    style.color = 'var(--accent)';
  }
  if (!src || !enlarges) {
    return (
      <span className="initials" style={style} aria-hidden={!src}>
        {src ? <img src={src} alt="" /> : text}
      </span>
    );
  }
  return (
    <>
      <button
        type="button"
        className="initials initials-tap"
        style={style}
        aria-label="Открыть фото"
        onClick={(event) => {
          // The circle often sits in a card that folds on a tap; this tap is the face's.
          event.stopPropagation();
          setFrom(event.currentTarget.getBoundingClientRect());
        }}
      >
        <img src={src} alt="" />
      </button>
      {from && createPortal(<FaceViewer src={src} from={from} onClose={() => setFrom(null)} />, document.body)}
    </>
  );
}

/** «Х420ТА» set the way a Russian plate is: small region, big number. */
export function Plate({ plate }: { plate: string }) {
  // Letter, three digits, two letters. Anything else is shown whole rather than cut
  // to fit a pattern it does not have.
  const match = /^(\D)(\d{3})(\D{2})/.exec(plate.trim());
  if (!match) return <span className="plate">{plate}</span>;
  return (
    <span className="plate">
      <span className="plate-side">{match[1]}</span>
      <span className="plate-number">{match[2]}</span>
      <span className="plate-side">{match[3]}</span>
    </span>
  );
}

/**
 * The star a ride is rated with, drawn rather than set: Manrope has no «★», and a
 * fallback glyph sits on its own font's baseline. Traced in the design's 12×12 box,
 * the same as the phone's `RatingStar`.
 */
export function RatingStar({ size = 12, color = 'currentColor' }: { size?: number; color?: string }) {
  const points: string[] = [];
  for (let i = 0; i < 10; i++) {
    const radius = i % 2 === 0 ? 5.2 : 2.4;
    const angle = -Math.PI / 2 + (i * Math.PI) / 5;
    points.push(`${(6 + radius * Math.cos(angle)).toFixed(2)},${(6.5 + radius * Math.sin(angle)).toFixed(2)}`);
  }
  return (
    <svg viewBox="0 0 12 12" width={size} height={size} aria-hidden="true" style={{ flex: 'none' }}>
      <polygon
        points={points.join(' ')}
        fill={color}
        stroke={color}
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// -------------------------------------------------------------------- sheets

/**
 * The bottom sheet the design uses for every modal: a rounded top, a title with a ✕,
 * and whatever else. A centred dialog on a desktop — the same element, anchored
 * differently.
 */
export function Sheet({
  title,
  hint,
  onClose,
  children,
}: {
  title: string;
  hint?: string;
  onClose: () => void;
  children: ReactNode;
}) {
  useEscape(true, onClose);

  useEffect(() => {
    // The page behind must not scroll under the sheet. On iOS this is also what stops
    // a swipe on the scrim dragging the whole app around.
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  return (
    <div
      className="scrim"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="sheet">
        <div className="sheet-head">
          <h2>{title}</h2>
          <IconButton icon="close" size={18} label="Закрыть" onClick={onClose} />
        </div>
        {hint && <p className="sheet-hint">{hint}</p>}
        <div className="sheet-body">{children}</div>
      </div>
    </div>
  );
}

/**
 * The sheet that asks once before something is taken away for good: the act in the
 * red «Отменить поездку» wears, and «Отмена» as the wider way out — or [keep], where
 * the act is itself a cancelling and «Отмена» under «Отменить поездку» would read as
 * a second way to do it.
 */
export function ConfirmSheet({
  title,
  hint,
  label,
  icon = 'delete_outline',
  keep = 'Отмена',
  busy = false,
  error,
  onConfirm,
  onClose,
  children,
}: {
  title: string;
  hint: string;
  label: string;
  icon?: IconName;
  keep?: string;
  busy?: boolean;
  error?: ApiError | null;
  onConfirm: () => void;
  onClose: () => void;
  /** Anything asked beside the question — «Также удалить у водителя». */
  children?: ReactNode;
}) {
  return (
    <Sheet title={title} hint={hint} onClose={onClose}>
      {children}
      <div className="stack" style={{ gap: 10 }}>
        <Button tone="stop" size="card" icon={icon} busy={busy} onClick={onConfirm}>
          {label}
        </Button>
        <Button tone="plain" size="card" onClick={onClose}>
          {keep}
        </Button>
      </div>
      <ErrorNote error={error} />
    </Sheet>
  );
}

// -------------------------------------------------------------------- lists

/** The row of a settings-style list, with a chevron or a switch on the right. */
export function ListRow({
  title,
  hint,
  news = false,
  trailing,
  onClick,
}: {
  title: string;
  hint?: ReactNode;
  /** The hint is news — a new version under «Приложение» — and is in the accent. */
  news?: boolean;
  trailing?: ReactNode;
  onClick?: () => void;
}) {
  const inner = (
    <>
      <span className="grow">
        <span className="list-row-title">{title}</span>
        {hint && <span className={news ? 'list-row-hint news' : 'list-row-hint'}>{hint}</span>}
      </span>
      {trailing}
    </>
  );
  // A row that does something is a button to the keyboard and to a screen reader —
  // but not a <button>, since what stands on its right may be one: the switch.
  return onClick ? (
    <div
      className="list-row tappable"
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick();
        }
      }}
    >
      {inner}
    </div>
  ) : (
    <div className="list-row">{inner}</div>
  );
}

/** The switch a setting is turned off with — the driver app's, in the accent. */
export function Switcher({
  on,
  onChange,
  label,
}: {
  on: boolean;
  onChange: (on: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      className="switcher"
      onClick={(event) => {
        // The row it stands in flips it too; one tap is one flip.
        event.stopPropagation();
        onChange(!on);
      }}
    />
  );
}

/**
 * The slide that stands where a button would be too cheap — «Выйти из аккаунта».
 *
 * One direction, near end to far, the way a call is answered: the act happens when
 * the knob is let go past nine tenths of the track and not before. A tap only nudges
 * the knob. A keyboard or a screen reader is given a plain button inside it, which a
 * deliberate press on is already the confirmation the slide is here to be.
 */
export function SwipeToConfirm({
  label,
  hint,
  armedHint,
  icon,
  onConfirm,
}: {
  label: string;
  hint: string;
  armedHint: string;
  icon: IconName;
  onConfirm: () => Promise<void> | void;
}) {
  const track = useRef<HTMLDivElement>(null);
  const start = useRef<{ x: number; from: number; moved: boolean } | null>(null);
  const [x, setX] = useState(0);
  const [travel, setTravel] = useState(0);
  const [busy, setBusy] = useState(false);
  const [settling, setSettling] = useState(false);

  useEffect(() => {
    const box = track.current;
    if (!box) return;
    const measure = () => setTravel(Math.max(0, box.clientWidth - 48 - 8));
    measure();
    const watch = new ResizeObserver(measure);
    watch.observe(box);
    return () => watch.disconnect();
  }, []);

  const progress = travel === 0 ? 0 : Math.min(1, Math.max(0, x / travel));
  const committed = progress >= 0.9;
  // Nothing for the first half of the pull, then all the way to the act's colour by
  // the moment letting go would do it.
  const heat = Math.min(1, Math.max(0, (progress - 0.5) / 0.4));

  const confirm = async () => {
    if (busy) return;
    setBusy(true);
    setX(travel);
    try {
      await onConfirm();
    } finally {
      // The act usually takes the screen away with it. When it does not, the knob
      // comes back rather than sitting at the far end looking spent.
      setBusy(false);
      setSettling(true);
      setX(0);
    }
  };

  const nudge = () => {
    setSettling(true);
    setX(18);
    window.setTimeout(() => setX(0), 120);
  };

  return (
    <div>
      <div
        ref={track}
        className="swipe"
        style={{
          background: `color-mix(in srgb, var(--stop-text) ${Math.round(progress * 28)}%, var(--stop-card))`,
        }}
        onPointerDown={(event) => {
          if (busy) return;
          event.currentTarget.setPointerCapture(event.pointerId);
          setSettling(false);
          start.current = { x: event.clientX, from: x, moved: false };
        }}
        onPointerMove={(event) => {
          const at = start.current;
          if (!at) return;
          const dx = event.clientX - at.x;
          if (Math.abs(dx) > 3) at.moved = true;
          setX(Math.min(travel, Math.max(0, at.from + dx)));
        }}
        onPointerUp={() => {
          const at = start.current;
          start.current = null;
          if (!at) return;
          if (!at.moved) nudge();
          else if (committed) void confirm();
          else {
            setSettling(true);
            setX(0);
          }
        }}
        onPointerCancel={() => {
          start.current = null;
          setSettling(true);
          setX(0);
        }}
      >
        <span
          className="swipe-words"
          style={{ color: 'var(--stop-text)', opacity: Math.max(0, 1 - progress * 3) }}
          aria-hidden="true"
        >
          {label}
        </span>
        <span
          className="swipe-knob"
          style={{
            transform: `translateX(${x}px)`,
            transition: settling ? 'transform 0.22s cubic-bezier(0.33, 1, 0.68, 1)' : undefined,
            background: `color-mix(in srgb, var(--stop-text) ${Math.round(heat * 100)}%, var(--raised))`,
            color: `color-mix(in srgb, var(--on-accent) ${Math.round(heat * 100)}%, var(--stop-text))`,
            border: `1px solid color-mix(in srgb, var(--stop-text) ${Math.round((1 - heat) * 28)}%, transparent)`,
          }}
        >
          {busy ? <span className="spinner" style={{ width: 20, height: 20 }} /> : <Icon name={icon} size={21} />}
        </span>
        <button
          type="button"
          className="visually-hidden"
          onClick={() => void confirm()}
          aria-description={hint}
        >
          {label}
        </button>
      </div>
      <p className="swipe-hint" aria-hidden="true">
        {committed ? armedHint : hint}
      </p>
    </div>
  );
}

// -------------------------------------------------------------------- fields

/**
 * `LabelledField`: the white card with a small uppercase label and a value inside it.
 * Every input in the design is one of these. The trailing — the eye — sits beside
 * the label and the input together, so a field with one is as tall as those without.
 */
export function LabelledField({
  label,
  optional = false,
  trailing,
  inputRef,
  ...input
}: InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  optional?: boolean;
  trailing?: ReactNode;
  inputRef?: React.Ref<HTMLInputElement>;
}) {
  return (
    <label className="lfield">
      <span className="grow">
        <span className="lfield-label">
          <span className="label">{label}</span>
          {optional && <span className="lfield-optional">не обязательно</span>}
        </span>
        <input ref={inputRef} {...input} />
      </span>
      {trailing}
    </label>
  );
}

/**
 * `GrowableBox`: a textarea of `minRows` with a corner to pull, up to `maxRows`.
 *
 * The corner is drawn and dragged here rather than left to CSS `resize`, because
 * phone browsers do not offer that one to a finger at all — Chrome and Firefox on
 * Android draw no grip, and Safari's cannot be pulled — and a phone is where nearly
 * every order is written. Pointer events cover the mouse and the thumb alike.
 */
export function GrowableBox({
  minRows = 2,
  maxRows = 12,
  ...textarea
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { minRows?: number; maxRows?: number }) {
  const ref = useRef<HTMLTextAreaElement>(null);
  // How far the corner has been pulled, on top of the rows that are always there —
  // the difference rather than a height, so two rows stay two rows when the
  // reader's text size changes under it.
  const [pulled, setPulled] = useState(0);
  const drag = useRef<{ id: number; y: number; from: number } | null>(null);

  const row = () => {
    const box = ref.current;
    return box ? parseFloat(getComputedStyle(box).lineHeight) || 21.6 : 21.6;
  };

  return (
    <div className="growable-box">
      <textarea
        ref={ref}
        {...textarea}
        className="growable"
        rows={minRows}
        style={{ height: `calc(${minRows} * 1.35em + ${pulled}px)` }}
      />
      <span
        className="growable-grip"
        aria-hidden="true"
        onPointerDown={(event) => {
          event.preventDefault();
          event.currentTarget.setPointerCapture(event.pointerId);
          drag.current = { id: event.pointerId, y: event.clientY, from: pulled };
        }}
        onPointerMove={(event) => {
          const start = drag.current;
          if (!start || start.id !== event.pointerId) return;
          const ceiling = (maxRows - minRows) * row();
          setPulled(Math.min(ceiling, Math.max(0, start.from + event.clientY - start.y)));
        }}
        onPointerUp={() => (drag.current = null)}
        onPointerCancel={() => (drag.current = null)}
      >
        <svg width="11" height="11" viewBox="0 0 11 11">
          <path d="M11 1 1 11M11 6 6 11" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
      </span>
    </div>
  );
}

/** «Телефон» — the same card, over the kit's input that keeps the +7 mask and the caret. */
export function PhoneField({
  value,
  onChange,
  autoFocus,
  onEnter,
}: {
  value: string;
  onChange: (phone: string) => void;
  autoFocus?: boolean;
  onEnter?: () => void;
}) {
  return (
    <label className="lfield">
      <span className="grow">
        <span className="lfield-label">
          <span className="label">Телефон</span>
        </span>
        <PhoneInput
          className=""
          autoComplete="tel username"
          placeholder="+7 900 000-00-00"
          value={value}
          autoFocus={autoFocus}
          onChange={onChange}
          onKeyDown={(event) => {
            if (event.key === 'Enter') onEnter?.();
          }}
        />
      </span>
    </label>
  );
}

/** The show/hide toggle inside a password field. */
export function EyeButton({ shown, onClick }: { shown: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      className="eye"
      onClick={onClick}
      aria-label={shown ? 'Скрыть пароль' : 'Показать пароль'}
      title={shown ? 'Скрыть пароль' : 'Показать пароль'}
    >
      <Icon name={shown ? 'visibility_off_outlined' : 'visibility_outlined'} size={19} />
    </button>
  );
}

/**
 * «Пароль» and «Повторите пароль». The confirmation is asked for while the field is
 * covered, and dropped the moment the eye opens: somebody reading back what they
 * typed has nothing left to confirm.
 */
export function PasswordPair({
  label = 'Пароль',
  repeatLabel = 'Повторите пароль',
  password,
  repeat,
  shown,
  onToggle,
  onPassword,
  onRepeat,
}: {
  label?: string;
  repeatLabel?: string;
  password: string;
  repeat: string;
  shown: boolean;
  onToggle: () => void;
  onPassword: (value: string) => void;
  onRepeat: (value: string) => void;
}) {
  return (
    <div className="stack" style={{ gap: 10 }}>
      <LabelledField
        label={label}
        type={shown ? 'text' : 'password'}
        autoComplete="new-password"
        value={password}
        onChange={(event) => onPassword(event.target.value)}
        trailing={<EyeButton shown={shown} onClick={onToggle} />}
      />
      {!shown && (
        <LabelledField
          label={repeatLabel}
          type="password"
          autoComplete="new-password"
          value={repeat}
          onChange={(event) => onRepeat(event.target.value)}
        />
      )}
    </div>
  );
}

// --------------------------------------------------------------------- routes

/** From, through, to — as the route line wants them. */
export function routeOf(stops: RideStopView[]): string[] {
  if (stops.length === 0) return [];
  const from = stops.find((s) => s.kind === 'PICKUP') ?? stops[0]!;
  const middle = stops.filter((s) => s.kind === 'STOP');
  const to = stops[stops.length - 1]!;
  return [from.address, ...middle.map((s) => s.address), to.address];
}

export const pickupOf = (stops: RideStopView[]): string =>
  (stops.find((s) => s.kind === 'PICKUP') ?? stops[0])?.address ?? '';

export const destinationOf = (stops: RideStopView[]): string =>
  stops[stops.length - 1]?.address ?? '';

/**
 * A route as a vertical list — a preorder card, anywhere a route is read rather than
 * edited: a ring for where you get in, hollow greys between, a solid dot at the end.
 */
export function RouteLine({ stops, dense = false }: { stops: string[]; dense?: boolean }) {
  if (stops.length < 2) return null;
  return (
    <div className={dense ? 'route-line dense' : 'route-line'}>
      {stops.map((text, i) => {
        const kind = i === 0 ? 'start' : i === stops.length - 1 ? 'end' : 'middle';
        return (
          <div className={`route-line-row ${kind}`} key={`${i}-${text}`}>
            <span className="route-rail">
              <span className={`route-ring ${kind}`} />
            </span>
            <span className="route-line-text">{text}</span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * The driver's number to transfer to, and the button that copies it — on «К оплате»,
 * and on a finished ride opened in «История». The whole row copies: a number is what
 * people aim at, not the icon at the end of it.
 */
export function TransferNumber({ phone }: { phone: string }) {
  const [copied, setCopied] = useState(false);

  // The check stands where the copy icon was for a couple of seconds.
  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(timer);
  }, [copied]);

  return (
    <button
      type="button"
      className="transfer"
      aria-label={`Скопировать номер ${prettyPhone(phone)}`}
      onClick={() => void copyText(phone).then(setCopied)}
    >
      <span className="grow ellipsis transfer-number">{prettyPhone(phone)}</span>
      <Icon
        name={copied ? 'check_rounded' : 'copy_rounded'}
        size={20}
        style={{ color: copied ? 'var(--go-text)' : 'var(--muted)' }}
      />
    </button>
  );
}

const STOP_LABEL = { PICKUP: 'Откуда', STOP: 'Остановка', DESTINATION: 'Куда' } as const;

/**
 * The whole route, labelled — a card in «История» once it is opened: «ОТКУДА»,
 * «ОСТАНОВКА», «КУДА» over each address, a dot level with each label, and one
 * unbroken thread through them all.
 */
export function RouteDetail({ stops }: { stops: RideStopView[] }) {
  return (
    <div>
      {stops.map((stop, i) => (
        <div className="threaded" key={`${i}-${stop.address}`}>
          <span
            className={`thread-rail ${i > 0 ? 'above' : ''} ${i < stops.length - 1 ? 'below' : ''}`}
            style={{ width: 10, '--line': '14.4px' } as CSSProperties}
          >
            <span className={`point-dot ${pointKind(stop.kind)}`} />
          </span>
          <div className="grow" style={{ marginLeft: 12, paddingBottom: i < stops.length - 1 ? 12 : 0 }}>
            <div className="label">{STOP_LABEL[stop.kind]}</div>
            <div className="route-address" style={{ marginTop: 3 }}>
              {stop.address}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * The route on a folded card in «Запланированные»: every point, without the words that
 * say which is which — the driver's folded order card, so the two apps draw one order
 * the same way. The labels are what opening adds, in [RouteDetail], whose dots and
 * thread these are. A port of `RouteSummary` in `route_dots.dart`.
 */
export function RouteSummary({ stops }: { stops: RideStopView[] }) {
  return (
    <div>
      {stops.map((stop, i) => (
        <div className="threaded" key={`${i}-${stop.address}`}>
          <span
            className={`thread-rail ${i > 0 ? 'above' : ''} ${i < stops.length - 1 ? 'below' : ''}`}
            style={{ width: 9, '--line': '18.225px' } as CSSProperties}
          >
            <span className={`point-dot ${pointKind(stop.kind)}`} />
          </span>
          <div
            className="grow route-summary-address"
            style={{ marginLeft: 9, paddingBottom: i < stops.length - 1 ? 5 : 0 }}
          >
            {stop.address}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Where a ride began and where it ended, and nothing between — a folded card in
 * «История», with the time each end was marked beside it.
 */
export function RouteEnds({
  from,
  to,
  fromTime,
  toTime,
}: {
  from: string;
  to: string;
  fromTime?: string | null;
  toTime?: string | null;
}) {
  const point = (address: string, time: string | null | undefined, first: boolean) => (
    <div className="row" style={{ alignItems: 'flex-start', gap: 0 }}>
      <span className="route-clock">{time ?? ''}</span>
      <div className="threaded grow">
        <span
          className={`thread-rail ${first ? 'below' : 'above'}`}
          style={{ width: 9, '--line': '18.85px' } as CSSProperties}
        >
          <span className={`point-dot ${first ? 'pickup' : 'destination'}`} />
        </span>
        <div className="grow route-address" style={{ marginLeft: 10, paddingBottom: first ? 6 : 0 }}>
          {address}
        </div>
      </div>
    </div>
  );
  return (
    <div>
      {point(from, fromTime, true)}
      {point(to, toTime, false)}
    </div>
  );
}

function pointKind(kind: RideStopView['kind']): string {
  return kind === 'PICKUP' ? 'pickup' : kind === 'STOP' ? 'stop' : 'destination';
}

/** The larger dot of the booking form, where a row is a target rather than a line. */
export function StopDot({ kind }: { kind: 'start' | 'middle' | 'end' }) {
  return <span className={`stop-dot ${kind === 'start' ? '' : kind}`} />;
}

/**
 * Whether what [content] holds fits in [space] — measured, as the phone measures its
 * text, rather than guessed at in pixels: a long day in the badge, a browser's own
 * text size and the figures themselves all move it. [shared] measures against the space
 * another `useFits` already holds, for a second thing that has to fit in the same room.
 */
export function useFits(
  shared?: RefObject<HTMLElement | null>,
): [RefObject<HTMLElement | null>, RefObject<HTMLElement | null>, boolean] {
  const own = useRef<HTMLElement | null>(null);
  const space = shared ?? own;
  const content = useRef<HTMLElement | null>(null);
  const [fits, setFits] = useState(true);
  useEffect(() => {
    const room = space.current;
    if (!room) return;
    const measure = () => setFits((content.current?.scrollWidth ?? 0) <= room.clientWidth);
    measure();
    const watch = new ResizeObserver(measure);
    watch.observe(room);
    // And what it holds, which can change while the room does not: «Отменяем…» is
    // longer than «Отменить», and a driver booked brings «Звонок» into a row.
    if (content.current) watch.observe(content.current);
    return () => watch.disconnect();
  }, []);
  return [space, content, fits];
}
